import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Loader2, CheckCircle2, ChevronRight, Sparkles, AlertTriangle, Copy, Lock, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

import {
  getUpcomingPayroll, getPayrollHistory, listSemesters,
  generatePayrollPeriod, confirmPayrollPayment, explainLedgerEntry, reconcileHistoricalPayroll,
  recordPayrollAdjustment, getMyPayrollPermissions, money,
  normalizePayrollMethod, PAYROLL_PAYMENT_METHODS,
  type PayrollLineItem, type LedgerEntry, type ReconciliationRow,
} from "@/lib/payroll";
import { PayrollReminderStatus } from "@/components/admin/payroll/PayrollReminderStatus";
import { toast } from "@/hooks/use-toast";
import { toast as sonner } from "sonner";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ExecutivePage, PageHero, ThreeQuestions, ExecutiveSummary, QuietSection,
  DecisionCard, QuietEmptyState, GardenStatusBadge,
} from "@/components/executive/system";
import type { GardenPhase } from "@/lib/gardenStatus";
import { MeetingOutcomePanel, type MeetingRow } from "@/components/admin/payroll/MeetingOutcomePanel";

/**
 * Every scheduled meeting for one class snapshot in this pay period — payable
 * meetings plus meetings already recorded as not taught — with the instructor's
 * normal per-meeting compensation attached.
 */
function buildMeetingRows(c: any): MeetingRow[] {
  const priced = (c?.meetings ?? []) as any[];
  // Normal per-meeting compensation: the assignment rate when the snapshot
  // carries one, otherwise the rate the engine priced this class's meetings at.
  const fallbackRate =
    Number(c?.pay_rate_amount ?? 0) * 100 ||
    Number(priced.find((m) => Number(m?.rate_cents) > 0)?.rate_cents ?? 0) ||
    (Number(c?.sessions_in_period ?? 0) > 0
      ? Math.round(Number(c?.subtotal_cents ?? 0) / Number(c.sessions_in_period))
      : 0);
  const byDate = new Map<string, number>();
  for (const m of priced) {
    if (m?.meeting_date) byDate.set(m.meeting_date, Number(m.rate_cents ?? fallbackRate));
  }
  for (const d of (c?.meeting_dates ?? []) as string[]) {
    if (!byDate.has(d)) byDate.set(d, fallbackRate);
  }
  for (const m of (c?.not_taught_meetings ?? []) as any[]) {
    if (m?.meeting_date && !byDate.has(m.meeting_date)) byDate.set(m.meeting_date, fallbackRate);
  }
  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([meeting_date, rate_cents]) => ({ meeting_date, rate_cents }));
}

const SUMMER_MONTHS = [5, 6, 7, 8] as const;

function monthName(m: number) { return new Date(Date.UTC(2000, m - 1, 1)).toLocaleString("en", { month: "long" }); }
function fmtDate(iso: string) {
  if (!iso) return "";
  return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}
function fmtDateShort(iso: string) {
  return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}
function semesterMonthLabel(semester?: string, month?: number | null) {
  const sem = semester ? semester.charAt(0).toUpperCase() + semester.slice(1) : "";
  return month ? `${sem} · ${monthName(month)}` : sem;
}
function ledgerStatus(entry: LedgerEntry) {
  if (entry.entry_kind === "adjustment") return "Correction";
  if (entry.entry_kind === "historical") return "Historical";
  return "Paid";
}

/** Translate a line-item into the Garden vocabulary + a plain-language status line. */
function itemStatus(
  item: PayrollLineItem,
  dueState: "upcoming" | "due_today" | "overdue" = "upcoming",
): { phase: GardenPhase; label: string } {
  if (item.status === "confirmed_paid") return { phase: "thriving", label: "Paid" };
  if (item.status === "exception") return { phase: "needs_care", label: "Cash exception" };
  if (item.status === "waiting_info") {
    const m = item.missing_info?.[0]?.toLowerCase() ?? "";
    if (m.includes("w-9") || m.includes("w9")) return { phase: "waiting", label: "Waiting on W-9" };
    if (m.includes("rate")) return { phase: "waiting", label: "Waiting on pay rate" };
    if (m.includes("agreement")) return { phase: "waiting", label: "Waiting on agreement" };
    if (m.includes("method") || m.includes("payment")) return { phase: "waiting", label: "Waiting on payment method" };
    return { phase: "waiting", label: `Waiting on ${item.missing_info[0] ?? "info"}` };
  }
  if (item.status === "skipped") return { phase: "wrapped_up", label: "Skipped" };
  if (dueState === "overdue") return { phase: "needs_care", label: "Overdue" };
  if (dueState === "due_today") return { phase: "needs_care", label: "Due Today" };
  return { phase: "growing", label: "Upcoming" };
}


export default function PayrollPage() {
  const qc = useQueryClient();
  const { user, isAdmin, loading: authLoading } = useAuth();
  // Defense in depth: AdminGuard already blocks mounting until auth resolves,
  // but never let a protected payroll call fire before the session + admin role
  // are hydrated — the edge function would answer 400 not_signed_in.
  const authReady = !authLoading && !!user && isAdmin;
  const { data: upcoming, isLoading } = useQuery({ queryKey: ["payroll", "upcoming"], queryFn: getUpcomingPayroll, enabled: authReady });
  const { data: history, isLoading: isHistoryLoading } = useQuery({ queryKey: ["payroll", "ledger"], queryFn: () => getPayrollHistory(), enabled: authReady });
  const { data: semesterInfo } = useQuery({ queryKey: ["payroll", "semesters"], queryFn: listSemesters, enabled: authReady });
  const { data: recon } = useQuery({ queryKey: ["payroll", "reconcile"], queryFn: reconcileHistoricalPayroll, enabled: authReady });
  const { data: perms } = useQuery({ queryKey: ["payroll", "perms"], queryFn: getMyPayrollPermissions, enabled: authReady });


  const canProcess = !!perms?.payroll_process;

  const [confirmTarget, setConfirmTarget] = useState<PayrollLineItem | null>(null);
  const [explainId, setExplainId] = useState<string | null>(null);

  const generateMut = useMutation({
    mutationFn: () => generatePayrollPeriod(upcoming?.period.pay_date),
    onSuccess: (r: any) => {
      toast({ title: "Payroll refreshed", description: `${r.line_items} instructors staged.` });
      qc.invalidateQueries({ queryKey: ["payroll"] });
    },
    onError: () => toast({ title: "Couldn't refresh payroll", description: "Try again in a moment.", variant: "destructive" }),
  });

  const ledgerRows = history?.rows ?? [];

  const lastPaidByInstructor = useMemo(() => {
    const map = new Map<string, string>();
    ledgerRows.forEach((r) => {
      const cur = map.get(r.instructor_id);
      if (!cur || r.paid_at > cur) map.set(r.instructor_id, r.paid_at);
    });
    return map;
  }, [ledgerRows]);

  const items = upcoming?.line_items ?? [];
  const readyItems = items.filter((l) => l.status === "ready" || l.status === "exception");
  const blockedItems = items.filter((l) => l.status === "waiting_info");
  const totalDue = readyItems.reduce((s, l) => s + (l.computed_amount_cents ?? 0), 0);
  // Obligation = everything we owe this period, blocked or not. Eligible = what can
  // actually be paid today. Blocked instructors are never hidden from the obligation.
  const obligationItems = items.filter((l) => l.status !== "skipped" && l.status !== "confirmed_paid");
  const obligationTotal = obligationItems.reduce((s, l) => s + (l.computed_amount_cents ?? 0), 0);
  const blockedTotal = blockedItems.reduce((s, l) => s + (l.computed_amount_cents ?? 0), 0);
  const pendingAdjustments = (recon?.rows ?? []).filter((r) => r.difference_cents !== 0).length;

  const payDate = upcoming?.period.pay_date;
  const daysLeft = upcoming?.days_remaining ?? 0;
  const dueState = (upcoming?.due_state ?? "upcoming") as "upcoming" | "due_today" | "overdue";
  const dueLabel = dueState === "overdue" ? "Overdue" : dueState === "due_today" ? "Due Today" : "Upcoming";
  const instructorWord = (count: number) => `${count} instructor${count === 1 ? "" : "s"}`;


  // Today's Focus — max 3 decisions.
  type Focus = {
    key: string; phase: GardenPhase; decision: string; because: string;
    primaryLabel: string; onPrimary: () => void;
  };
  const focuses: Focus[] = [];
  // Only surface "pay today" once we're actually on or past the pay date.
  const payDay = payDate ? new Date(payDate + "T00:00:00") : null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isPayDayOrLater = payDay ? today.getTime() >= payDay.getTime() : false;
  if (readyItems.length > 0 && isPayDayOrLater) {
    focuses.push({
      key: "confirm-first-ready",
      phase: "needs_care",
      decision: `Pay ${readyItems.length} instructor${readyItems.length === 1 ? "" : "s"} — ${money(totalDue)} total`,
      because: `Due ${payDate ? fmtDate(payDate) : "soon"}. Start with the first row below.`,
      primaryLabel: "Start paying",
      onPrimary: () => setConfirmTarget(readyItems[0]),
    });
  }
  if (blockedItems.length > 0) {
    const first = blockedItems[0];
    focuses.push({
      key: "unblock",
      phase: "waiting",
      decision: `Unblock ${first.instructor_name}`,
      because: `Missing ${first.missing_info.join(", ")}. ${blockedItems.length > 1 ? `${blockedItems.length - 1} other instructor${blockedItems.length - 1 === 1 ? "" : "s"} also waiting.` : ""}`.trim(),
      primaryLabel: "Open instructor",
      onPrimary: () => { window.location.href = "/admin/instructors"; },
    });
  }
  if (pendingAdjustments > 0) {
    focuses.push({
      key: "recon",
      phase: "needs_care",
      decision: `Review ${pendingAdjustments} historical adjustment${pendingAdjustments === 1 ? "" : "s"}`,
      because: "Corrections from prior payroll owners are pending in Accounting.",
      primaryLabel: "Open Accounting",
      onPrimary: () => {
        const el = document.getElementById("payroll-accounting");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      },
    });
  }
  const topFocuses = focuses.slice(0, 3);

  const whatChanged = readyItems.length === 0 && blockedItems.length === 0
    ? "Nothing new — no payroll is currently due."
    : `${readyItems.length} staged${isPayDayOrLater ? " (ready to pay today)" : ` for ${payDate ? fmtDate(payDate) : "next payroll"}`} · ${blockedItems.length} blocked.`;
  const needsAttention = readyItems.length > 0 && isPayDayOrLater
    ? `Confirm ${readyItems.length} payment${readyItems.length === 1 ? "" : "s"} totalling ${money(totalDue)}.`
    : blockedItems.length > 0
      ? `${blockedItems.length} instructor${blockedItems.length === 1 ? "" : "s"} waiting on missing info.`
      : readyItems.length > 0
        ? `Nothing to pay today — ${readyItems.length} instructor${readyItems.length === 1 ? "" : "s"} scheduled for ${payDate ? fmtDate(payDate) : "next payroll"}.`
        : "Nothing needs your attention right now.";
  const doNext = readyItems.length > 0 && isPayDayOrLater
    ? "Start with the first Ready to Pay row and click Confirm Paid."
    : blockedItems.length > 0
      ? "Open Instructors and collect the missing item."
      : "Enjoy the calm.";

  return (
    <ExecutivePage section="payroll">
      <UnseenReviewBar
        badge="payroll"
        note="Marking these read only clears the sidebar bubble. It does not finalize payroll, mark anyone paid, or change any financial record, and payroll reminder emails continue on their own schedule."
      />

      <PageHero
        eyebrow={<span>🌿 Payroll Desk</span>}
        title={
          upcoming
            ? isPayDayOrLater
              ? `Pay ${readyItems.length} instructor${readyItems.length === 1 ? "" : "s"} by ${fmtDate(upcoming.period.pay_date)}.`
              : `Next payroll ${fmtDate(upcoming.period.pay_date)} — ${readyItems.length} instructor${readyItems.length === 1 ? "" : "s"} staged.`
            : "No payroll is currently due."
        }
        subtitle={upcoming
          ? `${semesterMonthLabel(upcoming.period.semester, upcoming.period.semester_month)} · ${periodLabel(upcoming.period.type)} · pay period ${fmtDateShort(upcoming.period.period_start)}–${fmtDateShort(upcoming.period.period_end)}.`
          : "Silver Sage responsibility begins July 1, 2026."}
        actions={
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" asChild>
              <a href="/admin/ledger">Financial Ledger</a>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => generateMut.mutate()} disabled={generateMut.isPending}>
              {generateMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Refresh
            </Button>
          </div>
        }
      />

      <ThreeQuestions
        whatChanged={whatChanged}
        needsAttention={needsAttention}
        doNext={doNext}
        attentionCount={readyItems.length + blockedItems.length}
      />

      {upcoming && (
        <ExecutiveSummary
          stats={[
            { label: "Payday", value: fmtDate(upcoming.period.pay_date), hint: `${fmtDateShort(upcoming.period.period_start)}–${fmtDateShort(upcoming.period.period_end)} pay period` },
            { label: "Payroll status", value: dueState === "overdue" ? `Overdue by ${Math.abs(daysLeft)} days` : dueLabel, hint: dueState === "due_today" ? "Payment is due today" : dueState === "upcoming" ? `${Math.abs(daysLeft)} days until payday` : "Payment requires attention" },
            { label: "Instructors awaiting payment", value: instructorWord(obligationItems.length), hint: "Ready and blocked obligations" },
            { label: "Total payroll still owed", value: money(obligationTotal), hint: `${instructorWord(obligationItems.length)} awaiting payment` },
            { label: "Ready to pay", value: instructorWord(readyItems.length), hint: `${money(totalDue)} ready to pay` },
            { label: "Blocked / needs review", value: instructorWord(blockedItems.length), hint: `${money(blockedTotal)} currently blocked`, hideIfZero: false },
          ]}

        />
      )}

      {upcoming && <PayrollReminderStatus payDate={upcoming.period.pay_date} />}

      {/* Layer 1: Today's Focus */}
      {topFocuses.length > 0 && (
        <QuietSection
          eyebrow="Today's Focus"
          title="What to decide today"
          description="One decision at a time. Everything else can wait."
          paper={false}
        >
          <div className="grid gap-3">
            {topFocuses.map((f) => (
              <DecisionCard
                key={f.key}
                phase={f.phase}
                decision={f.decision}
                because={f.because}
                primaryLabel={f.primaryLabel}
                onPrimary={f.onPrimary}
              />
            ))}
          </div>
        </QuietSection>
      )}

      {/* Layer 2 & 3: Ready to Pay with expandable details */}
      <QuietSection
        eyebrow="Ready to Pay"
        title={upcoming ? `By ${fmtDate(upcoming.period.pay_date)}` : "Payroll roster"}
        description="One row per instructor. Expand any row to see the calculation."
      >
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Computing payroll…
          </div>
        ) : items.length === 0 ? (
          <QuietEmptyState message="No payroll due right now." detail="You'll see instructors here as the next pay date approaches." />
        ) : (
          <Accordion type="multiple" className="divide-y">
            {items.map((l) => (
              <InstructorRow
                key={l.instructor_id}
                item={l}
                payDate={upcoming!.period.pay_date}
                dueState={dueState}

                periodStart={upcoming!.period.period_start}
                periodEnd={upcoming!.period.period_end}
                lastPaid={lastPaidByInstructor.get(l.instructor_id) ?? null}
                canProcess={canProcess}
                onConfirm={() => setConfirmTarget(l)}
                onOutcomeChanged={() => generateMut.mutate()}
              />
            ))}
          </Accordion>
        )}
      </QuietSection>

      {/* Layer 4: Accounting — deliberately below the fold, collapsed. */}
      <div id="payroll-accounting">
        <QuietSection
          eyebrow="Accounting"
          title="Ledger, history & reconciliation"
          description="Auditor's corner. You don't need this to pay today."
          paper={false}
        >
          <Accordion type="multiple" className="space-y-3">
            <AccordionItem value="history" className="paper-card px-4 border-0">
              <AccordionTrigger className="text-sm">Payroll history</AccordionTrigger>
              <AccordionContent>
                <PayrollHistoryList rows={ledgerRows} loading={isHistoryLoading} onExplain={setExplainId} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ledger" className="paper-card px-4 border-0">
              <AccordionTrigger className="text-sm">Full ledger by semester</AccordionTrigger>
              <AccordionContent>
                <LedgerSemesterTabs rows={ledgerRows} current={semesterInfo?.current_semester ?? "summer"} onExplain={setExplainId} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="recon" className="paper-card px-4 border-0">
              <AccordionTrigger className="text-sm">
                <span className="flex items-center gap-2">
                  Historical reconciliation
                  {pendingAdjustments > 0 && (
                    <Badge variant="outline" className="text-terracotta border-terracotta/40">{pendingAdjustments} pending</Badge>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ReconciliationTable rows={recon?.rows ?? []} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </QuietSection>
      </div>

      <ConfirmDialog target={confirmTarget} payDate={upcoming?.period.pay_date} onClose={() => setConfirmTarget(null)} />
      <ExplainDialog ledgerId={explainId} onClose={() => setExplainId(null)} />
    </ExecutivePage>
  );
}

/* ------------------------- Layer 2 + 3: instructor rows ------------------------- */

/* --- Payment destination line + copy actions --- */
function copyToClipboard(text: string, label = "Copied") {
  try {
    navigator.clipboard.writeText(text);
    sonner.success(`${label}`);
  } catch {
    sonner.error("Copy failed");
  }
}

function PaymentDestinationRow({ item }: { item: PayrollLineItem }) {
  const d = item.payment_details;
  const method = (d?.method ?? item.payment_method_snapshot ?? "").toLowerCase();

  if (item.payment_details_missing) {
    return (
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
          <AlertTriangle className="h-3 w-3" /> Payment details needed
        </span>
        <Link
          to={`/admin/instructors?open=${item.instructor_id}`}
          className="text-xs text-primary hover:underline"
        >
          Open instructor record
        </Link>
      </div>
    );
  }

  if (method === "cash") {
    return <p className="text-xs text-muted-foreground mt-0.5">Cash payment</p>;
  }
  if (method === "check") {
    return <p className="text-xs text-muted-foreground mt-0.5">Check</p>;
  }
  if (method === "zelle" && d?.zelle_contact) {
    const kindLabel = d.zelle_recipient_type === "phone" ? "Zelle (phone)" : "Zelle";
    return (
      <div className="mt-0.5 flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">{kindLabel}:</span>
        <span className="text-foreground font-medium">{d.zelle_contact}</span>
        <button
          type="button"
          onClick={() => copyToClipboard(d.zelle_contact!, "Zelle information copied")}
          className="inline-flex items-center gap-1 text-primary hover:underline"
          aria-label="Copy Zelle information"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
    );
  }
  if ((method === "paypal" || method === "venmo") && d?.zelle_contact) {
    return (
      <div className="mt-0.5 flex items-center gap-2 text-xs">
        <span className="text-muted-foreground capitalize">{method}:</span>
        <span className="text-foreground font-medium">{d.zelle_contact}</span>
        <button
          type="button"
          onClick={() => copyToClipboard(d.zelle_contact!, `${method} info copied`)}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>
    );
  }
  return <p className="text-xs text-muted-foreground mt-0.5 capitalize">{method || "Payment method not set"}</p>;
}

function InstructorRow({
  item, payDate, dueState = "upcoming", periodStart, periodEnd, lastPaid, canProcess, onConfirm,
  onOutcomeChanged,
}: {
  item: PayrollLineItem; payDate: string; dueState?: "upcoming" | "due_today" | "overdue";
  periodStart: string; periodEnd: string;
  lastPaid: string | null; canProcess: boolean; onConfirm: () => void;
  onOutcomeChanged: () => void;
}) {
  const status = itemStatus(item, dueState);

  const canPay = (item.status === "ready" || item.status === "exception") && !item.payment_details_missing;
  const displayName = item.instructor_full_name || item.instructor_name;
  return (
    <AccordionItem value={item.instructor_id} className="border-0">
      <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground">{displayName}</span>
            <GardenStatusBadge phase={status.phase} label={status.label} />
          </div>
          <PaymentDestinationRow item={item} />
          {lastPaid ? (
            <p className="text-[11px] text-muted-foreground mt-0.5">Last paid {fmtDate(lastPaid)}</p>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-3 sm:block sm:text-right sm:shrink-0">
          <span className="text-xs text-muted-foreground sm:hidden">Amount still owed</span>
          <div className="font-heading text-lg text-foreground flex items-center gap-1 justify-end">
            {money(item.computed_amount_cents)}
            <button
              type="button"
              onClick={() => copyToClipboard((item.computed_amount_cents / 100).toFixed(2), "Amount copied")}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Copy amount"
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
        </div>
        <Button
          size="sm"
          onClick={onConfirm}
          disabled={!canPay || item.computed_amount_cents === 0 || !canProcess}
          className="gap-1 w-full sm:w-auto"
          title={!canProcess ? "Requires payroll_process permission" : undefined}
        >
          {!canProcess ? (<><Lock className="h-4 w-4" /> View only</>)
            : canPay ? (<><CheckCircle2 className="h-4 w-4" /> Mark Paid</>)
            : "Waiting"}
        </Button>
        <AccordionTrigger className="p-1 hover:no-underline self-end sm:self-auto" aria-label={`Show payroll calculation for ${displayName}`} />
      </div>
      <AccordionContent>
        <div className="pb-4 space-y-3 text-sm">
          <div className="text-xs text-muted-foreground">
            Pay period {fmtDateShort(periodStart)}–{fmtDateShort(periodEnd)} · pay by {fmtDate(payDate)}
          </div>
          {item.classes_snapshot.length === 0 ? (
            <div className="text-muted-foreground text-xs">No class breakdown available.</div>
          ) : (
            <div className="space-y-2">
              {item.classes_snapshot.map((c, i) => (
                <div key={i} className="rounded-md border bg-muted/20 p-3 text-xs">
                  <div className="text-sm font-medium text-foreground">
                    {c.class_title}
                    {c.session_label ? <span className="text-muted-foreground font-normal"> · {c.session_label}</span> : null}
                  </div>
                  {c.meeting_dates?.length ? (
                    <div className="mt-1 space-y-0.5">
                      <div>Meetings included: {c.meeting_dates.map(fmtDateShort).join(", ")}</div>
                      <div>
                        {c.sessions_in_period} × ${c.pay_rate_amount ?? "—"} ={" "}
                        <span className="font-semibold text-foreground">{money(Number(c.subtotal_cents ?? 0))}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground mt-1">No payable meetings in this period.</div>
                  )}
                  {c.previously_paid_dates?.length ? (
                    <div className="text-muted-foreground mt-1">
                      Already paid previously: {c.previously_paid_dates.map(fmtDateShort).join(", ")}
                    </div>
                  ) : null}
                  {(c as any).excluded_on_or_after_pay_date?.length ? (
                    <div className="text-muted-foreground mt-1">
                      Excluded (on or after pay date): {(c as any).excluded_on_or_after_pay_date.map(fmtDateShort).join(", ")}
                    </div>
                  ) : null}
                  {(c as any).not_taught_meetings?.length ? (
                    <div className="mt-1 rounded-md border border-border bg-muted/40 px-2 py-1">
                      {(c as any).not_taught_meetings.map((m: any) => (
                        <div key={m.meeting_date} className="text-muted-foreground">
                          Not payable — {fmtDateShort(m.meeting_date)}: {m.reason}
                          {m.covered_by ? ` (covered by ${m.covered_by})` : ""}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <MeetingOutcomePanel
                    instructorId={item.instructor_id}
                    instructorName={item.instructor_full_name || item.instructor_name}
                    classSlug={c.class_slug}
                    classTitle={c.class_title}
                    sessionLabel={c.session_label ?? null}
                    meetings={buildMeetingRows(c)}
                    onChanged={onOutcomeChanged}
                  />
                </div>

              ))}
            </div>
          )}
          {item.missing_info.length > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-foreground">
              <div className="font-medium mb-0.5 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Missing information</div>
              <div className="text-muted-foreground">{item.missing_info.join(", ")}</div>
              <SendOnboardingRequestButton
                instructorId={item.instructor_id}
                instructorName={item.instructor_name}
              />
            </div>
          )}

          {item.is_exception && item.exception_reason && (
            <div className="rounded-md border border-terracotta/30 bg-terracotta/5 p-3 text-xs whitespace-pre-wrap text-foreground/90">
              <div className="font-medium mb-0.5 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Why this is an exception</div>
              {item.exception_reason}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Meetings before July 1, 2026, on or after the pay date, or already paid on a prior ledger row are excluded automatically.
          </p>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

/** Sends (or re-sends) the secure onboarding packet that unblocks payroll. */
function SendOnboardingRequestButton({
  instructorId, instructorName,
}: { instructorId: string; instructorName: string }) {
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-onboarding-packet", {
        body: { instructorId },
      });
      if (error) throw error;
      const code = (data as any)?.error;
      if (code === "requires_approval" || code === "requires_reapproval") {
        throw new Error("This onboarding packet must be previewed and approved on the Instructors page before it can be sent.");
      }
      if (code) throw new Error(String(code));

      sonner.success(`Onboarding request sent to ${instructorName}`, {
        description: "They'll get a secure link for only the items still missing.",
      });
    } catch (e: any) {
      sonner.error("Could not send the onboarding request", { description: e?.message ?? "Please try again." });
    } finally {
      setSending(false);
    }
  };
  return (
    <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={send} disabled={sending}>
      {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
      Send onboarding request
    </Button>
  );
}

/* ------------------------- Layer 4: Accounting pieces ------------------------- */


function PayrollHistoryList({
  rows, loading, onExplain,
}: { rows: LedgerEntry[]; loading: boolean; onExplain: (id: string) => void }) {
  const historyGroups = useMemo(() => {
    const map = new Map<string, LedgerEntry[]>();
    rows.forEach((r) => {
      if (!map.has(r.paid_at)) map.set(r.paid_at, []);
      map.get(r.paid_at)!.push(r);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [rows]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (historyGroups.length === 0) {
    return <div className="text-sm text-muted-foreground py-4">No payroll history yet.</div>;
  }
  return (
    <div className="space-y-5">
      {historyGroups.map(([payDate, group]) => {
        const total = group.reduce((s, r) => s + Number(r.final_cents), 0);
        return (
          <div key={payDate} className="rounded-md border">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-muted/30 border-b">
              <div>
                <div className="font-medium text-sm">{fmtDate(payDate)}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {semesterMonthLabel(group[0]?.semester, group[0]?.semester_month)} · {group.length} {group.length === 1 ? "entry" : "entries"}
                </div>
              </div>
              <div className="text-sm font-semibold">{money(total)}</div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instructor</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs">{r.instructors?.preferred_name || r.instructors?.full_name}</TableCell>
                    <TableCell className="text-xs capitalize">{r.payment_method}</TableCell>
                    <TableCell className="text-xs"><Badge variant="secondary">{ledgerStatus(r)}</Badge></TableCell>
                    <TableCell className={`text-right font-semibold ${Number(r.final_cents) < 0 ? "text-destructive" : ""}`}>{money(Number(r.final_cents))}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => onExplain(r.id)}>Explain</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        );
      })}
    </div>
  );
}

function LedgerSemesterTabs({
  rows, current, onExplain,
}: { rows: LedgerEntry[]; current: string; onExplain: (id: string) => void }) {
  const bySemester = useMemo(() => {
    const map: Record<string, LedgerEntry[]> = { spring: [], summer: [], fall: [] };
    rows.forEach((r) => { (map[r.semester] ??= []).push(r); });
    return map;
  }, [rows]);
  const summerByMonth = useMemo(() => {
    const map: Record<number, LedgerEntry[]> = {};
    bySemester.summer.forEach((r) => {
      const m = r.semester_month ?? new Date(r.paid_at).getUTCMonth() + 1;
      (map[m] ??= []).push(r);
    });
    return map;
  }, [bySemester]);

  return (
    <Tabs defaultValue={current}>
      <TabsList>
        <TabsTrigger value="spring">Spring</TabsTrigger>
        <TabsTrigger value="summer">Summer</TabsTrigger>
        <TabsTrigger value="fall">Fall</TabsTrigger>
      </TabsList>
      <TabsContent value="spring"><LedgerTable rows={bySemester.spring} onExplain={onExplain} /></TabsContent>
      <TabsContent value="summer">
        <Tabs defaultValue={String(new Date().getUTCMonth() + 1)}>
          <TabsList>
            {SUMMER_MONTHS.map((m) => (
              <TabsTrigger key={m} value={String(m)}>
                {monthName(m)} ({(summerByMonth[m] ?? []).length})
              </TabsTrigger>
            ))}
          </TabsList>
          {SUMMER_MONTHS.map((m) => (
            <TabsContent key={m} value={String(m)}>
              <LedgerTable rows={summerByMonth[m] ?? []} onExplain={onExplain} />
            </TabsContent>
          ))}
        </Tabs>
      </TabsContent>
      <TabsContent value="fall"><LedgerTable rows={bySemester.fall} onExplain={onExplain} /></TabsContent>
    </Tabs>
  );
}

function LedgerTable({ rows, onExplain }: { rows: LedgerEntry[]; onExplain: (id: string) => void }) {
  if (!rows?.length) return <div className="text-sm text-muted-foreground py-4">No ledger entries yet.</div>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Paid</TableHead>
          <TableHead>Instructor</TableHead>
          <TableHead>Method</TableHead>
          <TableHead>Kind</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="text-right"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="text-xs">{fmtDate(r.paid_at)}</TableCell>
            <TableCell className="text-xs">{r.instructors?.preferred_name || r.instructors?.full_name}</TableCell>
            <TableCell className="text-xs capitalize">{r.payment_method}</TableCell>
            <TableCell className="text-xs"><Badge variant="outline">{r.entry_kind}</Badge></TableCell>
            <TableCell className={`text-right font-semibold ${Number(r.final_cents) < 0 ? "text-destructive" : ""}`}>{money(Number(r.final_cents))}</TableCell>
            <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => onExplain(r.id)}>Explain</Button></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ReconciliationTable({ rows }: { rows: ReconciliationRow[] }) {
  const qc = useQueryClient();
  const adjustMut = useMutation({
    mutationFn: async (row: ReconciliationRow) => recordPayrollAdjustment({
      instructor_id: row.instructor_id,
      corrects_ledger_id: row.stored_ledger_id ?? undefined,
      delta_cents: row.difference_cents,
      payment_method: "adjustment",
      paid_at: "2026-07-16",
      notes: `Historical reconciliation. Previously calculated: ${money(row.stored_cents)}. Pay this payroll: ${money(row.correct_gross_cents)}. Adjustment: ${row.difference_cents >= 0 ? "+" : ""}${money(row.difference_cents)}.`,
    }),
    onSuccess: () => {
      toast({ title: "Adjustment recorded", description: "Correction appended to the ledger." });
      qc.invalidateQueries({ queryKey: ["payroll"] });
    },
    onError: () => toast({ title: "Couldn't save that adjustment", description: "Try again in a moment.", variant: "destructive" }),
  });

  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground py-2">No historical rows to reconcile.</div>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Instructor</TableHead>
          <TableHead>Meetings included</TableHead>
          <TableHead className="text-right">Previously calculated</TableHead>
          <TableHead className="text-right">Pay this payroll</TableHead>
          <TableHead className="text-right">Change needed</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.instructor_id}>
            <TableCell className="font-medium align-top">{r.instructor_name}</TableCell>
            <TableCell className="text-xs align-top">
              {r.eligible.length === 0 && <span className="text-muted-foreground">No eligible meetings.</span>}
              {r.eligible.map((e, i) => (
                <div key={i}>
                  <span className="font-medium">{e.class_title}</span>
                  {e.session_label ? ` · ${e.session_label}` : ""} — {e.meeting_dates.map(fmtDateShort).join(", ") || "none"} = {money(Number(e.subtotal_cents ?? 0))}
                </div>
              ))}
            </TableCell>
            <TableCell className="text-right align-top">{money(r.stored_cents)}</TableCell>
            <TableCell className="text-right font-semibold align-top">{money(r.correct_gross_cents)}</TableCell>
            <TableCell className={`text-right align-top font-semibold ${r.difference_cents === 0 ? "text-muted-foreground" : r.difference_cents < 0 ? "text-emerald-600" : "text-destructive"}`}>
              {r.difference_cents === 0 ? "matches" : `${r.difference_cents > 0 ? "+" : ""}${money(r.difference_cents)}`}
            </TableCell>
            <TableCell className="text-right align-top">
              <Button
                size="sm"
                variant={r.difference_cents === 0 ? "ghost" : "default"}
                disabled={r.difference_cents === 0 || adjustMut.isPending || !r.stored_ledger_id}
                onClick={() => {
                  if (confirm(`Append a linked correction of ${money(r.difference_cents)} for ${r.instructor_name}?`)) {
                    adjustMut.mutate(r);
                  }
                }}
              >
                {r.difference_cents === 0 ? "OK" : "Approve"}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/* ------------------------- shared bits ------------------------- */

function periodLabel(type: string) {
  if (type === "mid_month") return "Mid-month (15th)";
  if (type === "end_month") return "End of month";
  if (type === "summer_month") return "Summer month";
  if (type === "special") return "Special";
  return type;
}

function ConfirmDialog({ target, payDate, onClose }: { target: PayrollLineItem | null; payDate?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const displayName = target?.instructor_full_name || target?.instructor_name || "";
  // The instructor record stores methods like "Zelle" — normalize to the exact
  // database enum value so the select actually shows the pre-filled choice.
  const defaultMethod = normalizePayrollMethod(target?.payment_details?.method ?? target?.payment_method_snapshot);
  const defaultDestination = target?.payment_details?.zelle_contact ?? "";

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<string>(defaultMethod);
  const [destination, setDestination] = useState<string>(defaultDestination);
  const [paidAt, setPaidAt] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmedIntent, setConfirmedIntent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset dialog when target changes
  useMemo(() => {
    setAmount("");
    setMethod(normalizePayrollMethod(target?.payment_details?.method ?? target?.payment_method_snapshot));
    setDestination(target?.payment_details?.zelle_contact ?? "");
    setReference("");
    setNotes("");
    setPaidAt(new Date().toISOString().slice(0, 10));
    setConfirmedIntent(false);
    setFormError(null);
  }, [target?.instructor_id]);

  const mut = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("no target");
      const chosen = normalizePayrollMethod(method);
      if (!chosen) throw new Error("Please select how this instructor was paid.");
      const requiresDest = ["zelle", "paypal", "venmo"].includes(chosen);
      if (requiresDest && !destination.trim()) throw new Error("Payment destination is required for electronic methods.");
      const parsedAmount = amount.trim() ? Number(amount.replace(/[$,\s]/g, "")) : null;
      if (parsedAmount !== null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
        throw new Error("Enter a valid payment amount.");
      }
      // Always pay against the payroll line item actually displayed — never the
      // "next" period. Falling back to the upcoming pay date used to settle the
      // wrong period (and tripped the duplicate guard) whenever the visible row
      // belonged to an older, still-outstanding period.
      const lineItemId = target.line_item_id ?? null;
      if (!lineItemId) {
        throw new Error("This payroll row isn't linked to a pay period yet. Click Refresh, then try again.");
      }
      return confirmPayrollPayment({
        line_item_id: lineItemId,
        amount_cents: parsedAmount !== null ? Math.round(parsedAmount * 100) : undefined,
        payment_method: chosen,
        notes: notes || undefined,
        // Actual payment date — independent of the July 31 pay-period due date.
        paid_at: paidAt,
        reference_number: reference || undefined,
        payment_destination_snapshot: {
          method: chosen,
          destination: destination || null,
          zelle_recipient_type: target.payment_details?.zelle_recipient_type ?? null,
          instructor_full_name: target.instructor_full_name ?? target.instructor_name,
          captured_at: new Date().toISOString(),
        },
      });
    },
    onSuccess: (res: any) => {
      if (res?.already_paid) {
        toast({
          title: "Already recorded",
          description: res.message ?? "This payroll item has already been recorded as paid.",
        });
      } else {
        toast({ title: "Payment recorded", description: `${displayName} — logged to the payroll ledger.` });
      }
      qc.invalidateQueries({ queryKey: ["payroll"] });
      qc.invalidateQueries({ queryKey: ["financial-ledger"] });
      onClose();
    },
    onError: (e: any) => {
      const msg = e?.message && !/non-2xx/i.test(e.message)
        ? e.message
        : "We couldn't record this payment. Nothing was posted to the ledger. Please try again.";
      setFormError(msg);
      toast({ title: "Couldn't record that payment", description: msg, variant: "destructive" });
    },
  });

  if (!target) return null;
  const meetingDates = target.classes_snapshot.flatMap((c) => c.meeting_dates ?? []);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mark Paid — {displayName}</DialogTitle>
          <DialogDescription>
            One immutable ledger entry will be written. This cannot be edited — corrections use a separate adjustment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md border bg-muted/20 p-3 space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Pay period</span>
              <span className="font-medium">{fmtDateShort(target.pay_rate_snapshot?.pay_period?.start)}–{fmtDateShort(target.pay_rate_snapshot?.pay_period?.end)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Classes</span>
              <span className="font-medium text-right">{target.classes_snapshot.map((c) => c.class_title).join(", ")}</span></div>
            {meetingDates.length > 0 && (
              <div className="flex justify-between gap-3"><span className="text-muted-foreground">Meetings</span>
                <span className="font-medium text-right">{meetingDates.map(fmtDateShort).join(", ")}</span></div>
            )}
            <div className="flex justify-between pt-1 border-t"><span className="text-muted-foreground">Total due</span>
              <span className="font-semibold text-base">{money(target.computed_amount_cents)}</span></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount paid (USD)</Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)}
                placeholder={(target.computed_amount_cents / 100).toFixed(2)} />
            </div>
            <div>
              <Label>Payment date</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Actual date sent. The record stays linked to the {fmtDate(target.pay_rate_snapshot?.pay_period?.pay_date ?? payDate ?? paidAt)} pay period.
              </p>
            </div>
          </div>

          <div>
            <Label>Payment method <span className="text-terracotta">*</span></Label>
            <select
              value={method}
              onChange={(e) => { setMethod(e.target.value); setFormError(null); }}
              className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Select method…</option>
              {PAYROLL_PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            {!method && (
              <p className="mt-1 text-[11px] text-muted-foreground">Please select how this instructor was paid.</p>
            )}
          </div>

          {["zelle", "paypal", "venmo"].includes(method.toLowerCase()) && (
            <div>
              <Label>Destination (email or phone)</Label>
              <Input value={destination} onChange={(e) => setDestination(e.target.value)}
                placeholder={target.payment_details?.zelle_contact ?? "recipient@example.com"} />
            </div>
          )}

          <div>
            <Label>Reference / transaction # (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Signed receipt filed…" />
          </div>

          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={confirmedIntent} onChange={(e) => setConfirmedIntent(e.target.checked)}
              className="mt-0.5" />
            <span>I have sent this payment and confirm the destination above is correct.</span>
          </label>

          {formError && (
            <div className="rounded-md border border-terracotta/40 bg-terracotta/5 p-3 text-xs text-terracotta">
              {formError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !confirmedIntent || !method}>
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Confirm Payment
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExplainDialog({ ledgerId, onClose }: { ledgerId: string | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    enabled: !!ledgerId,
    queryKey: ["payroll", "explain", ledgerId],
    queryFn: () => explainLedgerEntry(ledgerId!),
  });
  const e = data?.entry;
  const chain = (data as any)?.chain ?? [];
  return (
    <Dialog open={!!ledgerId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ledger entry breakdown</DialogTitle>
          <DialogDescription>
            {e ? `${e.instructors?.preferred_name || e.instructors?.full_name} — paid ${fmtDate(e.paid_at)} · ${e.payment_method}` : ""}
          </DialogDescription>
        </DialogHeader>
        {isLoading && <div className="text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…</div>}
        {e && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="rounded-md border p-2 bg-background/60"><div className="eyebrow">Gross</div><div className="text-lg font-semibold">{money(Number(e.gross_cents))}</div></div>
              <div className="rounded-md border p-2 bg-background/60"><div className="eyebrow">Adjustments</div><div className="text-lg font-semibold">{money(Number(e.adjustments_cents))}</div></div>
              <div className="rounded-md border p-2 bg-background/60"><div className="eyebrow">Final</div><div className="text-lg font-semibold">{money(Number(e.final_cents))}</div></div>
            </div>
            {chain.length > 0 && (
              <div>
                <div className="eyebrow mb-1">Correction chain</div>
                <div className="space-y-1">
                  {chain.map((c: any) => (
                    <div key={c.id} className="rounded border p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span>
                          <Badge variant="outline" className="mr-1">{c.relation === "corrects" ? "This corrects" : "Corrected by"}</Badge>
                          {c.entry_kind} · {fmtDate(c.paid_at)} · {c.payment_method}
                        </span>
                        <span className={Number(c.final_cents) < 0 ? "text-destructive font-semibold" : "font-semibold"}>{money(Number(c.final_cents))}</span>
                      </div>
                      {c.notes && <div className="text-muted-foreground mt-1">{c.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="eyebrow mb-1">Meetings paid</div>
              {Array.isArray(e.classes_snapshot) && e.classes_snapshot.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Class</TableHead>
                      <TableHead>Meeting dates</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {e.classes_snapshot.map((c: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{c.class_title}{c.session_label ? ` · ${c.session_label}` : ""}</TableCell>
                        <TableCell className="text-xs">{(c.meeting_dates ?? []).map(fmtDateShort).join(", ") || `${c.sessions_in_period ?? 0} meetings`}</TableCell>
                        <TableCell className="text-xs">${c.pay_rate_amount ?? "—"} {c.pay_rate_type ?? ""}</TableCell>
                        <TableCell className="text-right font-semibold">{money(Number(c.subtotal_cents ?? 0))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <div className="text-xs text-muted-foreground">No class breakdown recorded.</div>}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Ledger ID: <code>{e.id}</code> · Hash: <code>{e.immutable_hash?.slice(0, 16)}…</code>
              {(e as any).voided_at ? <> · <span className="text-destructive font-semibold">VOIDED</span></> : null}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
